import React from 'react';
import Icon, { ICONS } from '../../icons';
import type { LocalizationStrings } from '../../../../config/localization';

interface PricingFormulaHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  t: LocalizationStrings;
}

// Only these variables are automatically calculated by the system
// Exported so tests can assert parity with localization and EXAMPLE_FORMULAS evaluability.
export const AUTO_VARIABLES = [
  { name: 'input_img_mpx', descriptionKey: 'input_img_mpx' as const },
  { name: 'input_img_count', descriptionKey: 'input_img_count' as const },
  { name: 'input_img_count_may_zero', descriptionKey: 'input_img_count_may_zero' as const },
  { name: 'target_img_mpx', descriptionKey: 'target_img_mpx' as const },
  { name: 'target_img_width', descriptionKey: 'target_img_width' as const },
  { name: 'target_img_height', descriptionKey: 'target_img_height' as const },
  { name: 'target_long_side', descriptionKey: 'target_long_side' as const },
  { name: 'target_short_side', descriptionKey: 'target_short_side' as const },
  { name: 'target_aspect_ratio', descriptionKey: 'target_aspect_ratio' as const },
  { name: 'audio_duration', descriptionKey: 'audio_duration' as const },
  { name: 'video_duration', descriptionKey: 'video_duration' as const },
  { name: 'has_audio', descriptionKey: 'has_audio' as const },
  { name: 'has_video', descriptionKey: 'has_video' as const },
  { name: 'audio_duration_may_zero', descriptionKey: 'audio_duration_may_zero' as const },
  { name: 'video_duration_may_zero', descriptionKey: 'video_duration_may_zero' as const },
  { name: 'audio_duration_exact', descriptionKey: 'audio_duration_exact' as const },
  { name: 'video_duration_exact', descriptionKey: 'video_duration_exact' as const },
  { name: 'audio_duration_exact_may_zero', descriptionKey: 'audio_duration_exact_may_zero' as const },
  { name: 'video_duration_exact_may_zero', descriptionKey: 'video_duration_exact_may_zero' as const },
  { name: 'video_width', descriptionKey: 'video_width' as const },
  { name: 'video_height', descriptionKey: 'video_height' as const },
  { name: 'video_mpx', descriptionKey: 'video_mpx' as const },
  { name: 'prompt_length_1k', descriptionKey: 'prompt_length_1k' as const },
  { name: 'prompt_length_1k_may_zero', descriptionKey: 'prompt_length_1k_may_zero' as const },
  { name: 'prompt_length_ceil_100', descriptionKey: 'prompt_length_ceil_100' as const },
  { name: 'prompt_length_ceil_100_may_zero', descriptionKey: 'prompt_length_ceil_100_may_zero' as const },
  { name: 'text_length_ceil_100', descriptionKey: 'text_length_ceil_100' as const },
  { name: 'text_length_ceil_100_may_zero', descriptionKey: 'text_length_ceil_100_may_zero' as const },
  { name: 'has_prompt', descriptionKey: 'has_prompt' as const },
];

export const EXAMPLE_FORMULAS = [
  { formula: '100', comment: 'Fixed price' },
  { formula: '100 * num_images', comment: 'Per image' },
  { formula: '10 + (input_img_mpx * 20) + (target_img_mpx * 15)', comment: 'Based on resolution' },
  { formula: '40 + input_img_count * 5', comment: 'Per reference image' },
  { formula: 'Math.max(10, num_images * 5)', comment: 'Minimum 10 tokens' },
  { formula: 'quality === "hd" ? 100 : 50', comment: 'Conditional' },
  { formula: 'Math.max(audio_duration_may_zero, video_duration_may_zero) * 40', comment: 'Dubbing (audio or video)' },
  { formula: '100 + (has_video > 0 ? video_duration_exact_may_zero * 140 : 0)', comment: 'Base price + optional video, billed per fractional second' },
  { formula: 'has_audio > 0 ? audio_duration_may_zero * 40 : 0', comment: 'Conditional on audio presence' },
  { formula: 'quality === "high" ? (target_long_side >= 3840 ? 410 : target_long_side >= 2560 ? 230 : 150) : 50', comment: 'Per-tier pricing by long side' },
];

const PricingFormulaHelpModal: React.FC<PricingFormulaHelpModalProps> = ({
  isOpen,
  onClose,
  t
}) => {
  if (!isOpen) return null;

  const help = t.admin?.pricingFormulaHelp;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 animate-fadeIn p-4"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="bg-gray-900 border-2 border-yellow-500/50 rounded-xl max-w-2xl w-full max-h-[85vh] overflow-hidden animate-scaleIn shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-900/40 to-purple-900/30 px-6 py-4 border-b border-yellow-500/30 flex items-center justify-between">
          <h3 className="text-xl font-bold text-white flex items-center gap-3">
            <Icon name={ICONS.HELP} size={24} className="text-yellow-400" />
            {help?.title || 'Pricing Formula Help'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-gray-800 rounded"
          >
            <Icon name={ICONS.X} size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)] space-y-6">
          {/* Dynamic Variables */}
          <section>
            <h4 className="text-lg font-semibold text-yellow-300 mb-3 flex items-center gap-2">
              <Icon name={ICONS.ZAPS} size={18} className="text-yellow-400" />
              {help?.dynamicVariablesTitle || 'Dynamic Variables'}
            </h4>
            <div className="bg-gray-800/50 rounded-lg p-4 text-sm text-gray-300">
              <p className="mb-2">{help?.dynamicVariablesDesc || 'Any parameter from workflow inputs can be used in the formula. Variable names match the input widget names.'}</p>
              <p className="text-gray-400 text-xs">{help?.dynamicVariablesExamples || 'Examples: num_images, width, height, quality, duration, steps, etc.'}</p>
            </div>
          </section>

          {/* Auto-calculated Variables */}
          <section>
            <h4 className="text-lg font-semibold text-purple-300 mb-3 flex items-center gap-2">
              <Icon name={ICONS.SETTINGS} size={18} className="text-purple-400" />
              {help?.autoVariablesTitle || 'Auto-calculated Variables'}
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Variable</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">{help?.descriptionColumn || 'Description'}</th>
                  </tr>
                </thead>
                <tbody>
                  {AUTO_VARIABLES.map((variable) => (
                    <tr key={variable.name} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-2 px-3">
                        <code className="text-purple-300 bg-gray-800 px-2 py-0.5 rounded font-mono text-xs">
                          {variable.name}
                        </code>
                      </td>
                      <td className="py-2 px-3 text-gray-300">
                        {help?.variables?.[variable.descriptionKey] || variable.descriptionKey}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Examples */}
          <section>
            <h4 className="text-lg font-semibold text-green-300 mb-3 flex items-center gap-2">
              <Icon name={ICONS.CODE} size={18} className="text-green-400" />
              {help?.examplesTitle || 'Formula Examples'}
            </h4>
            <div className="bg-gray-800/50 rounded-lg p-4 space-y-2">
              {EXAMPLE_FORMULAS.map((item, index) => (
                <div key={index} className="font-mono text-sm flex items-start gap-2">
                  <span className="text-gray-600 select-none">{'>'}</span>
                  <code className="text-green-300">{item.formula}</code>
                  <span className="text-gray-500 text-xs ml-auto">// {item.comment}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Operations */}
          <section>
            <h4 className="text-lg font-semibold text-blue-300 mb-3 flex items-center gap-2">
              <Icon name={ICONS.INFO} size={18} className="text-blue-400" />
              {help?.operationsTitle || 'Supported Operations'}
            </h4>
            <div className="space-y-2 text-sm">
              <p className="text-gray-300">
                <span className="text-blue-400 font-medium">1.</span> {help?.operations?.arithmetic || 'Arithmetic: +, -, *, /, %'}
              </p>
              <p className="text-gray-300">
                <span className="text-blue-400 font-medium">2.</span> {help?.operations?.comparison || 'Comparison: >, <, >=, <=, ===, !=='}
              </p>
              <p className="text-gray-300">
                <span className="text-blue-400 font-medium">3.</span> {help?.operations?.ternary || 'Ternary: condition ? value1 : value2'}
              </p>
              <p className="text-gray-300">
                <span className="text-blue-400 font-medium">4.</span> {help?.operations?.math || 'Math: Math.ceil(), Math.floor(), Math.round(), Math.min(), Math.max(), Math.abs()'}
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PricingFormulaHelpModal;
